import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../Axios/axiosInstance";
import { Helmet } from "react-helmet";
import dayjs from "dayjs";

import {
  TextField,
  Button,
  Grid,
  Typography,
  Divider,
  Box,
  Snackbar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

const EditCompany = () => {
  const { establishmentId } = useParams();
  const [companyData, setCompanyData] = useState(null);

  const [editMode, setEditMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [openSuccess, setOpenSuccess] = useState(false);

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const response = await axiosInstance.get(
          `/api/getCompanyRegistrations/${establishmentId}`
        );
        setCompanyData(response.data);
      } catch (error) {
        console.error("Error fetching company data:", error);
      }
    };

    fetchCompanyData();
  }, [establishmentId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleEditClick = () => {
    setEditMode(true); // Enable edit mode when edit icon is clicked
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.put(
        `/api/updateCompany/${establishmentId}`,
        companyData
      );
      setSuccessMessage("Company details updated successfully!");
      setOpenSuccess(true);
      setEditMode(false); // Disable edit mode after successful submission

      // Hide success message after 3 seconds (3000 milliseconds)
      setTimeout(() => {
        setOpenSuccess(false);
        setSuccessMessage("");
      }, 3000);
    } catch (error) {
      console.error("Error updating company:", error);
    }
  };

  const [errors] = useState({
    emailid: false,
    ephonenumber: false,
    contactpersonname: false,
  });

  const handleSuccessClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSuccess(false);
  };

  if (!companyData) {
    return <div>Loading...</div>;
  }

  const handleDateChange = (date) => {
    if (date) {
      // Check if the date is valid before converting it
      if (date.isValid()) {
        setCompanyData((prevData) => ({
          ...prevData,
          registrationdate: date.toISOString(),
        }));
      }
    } else {
      setCompanyData((prevData) => ({
        ...prevData,
        registrationdate: null,
      }));
    }
  };

  return (
    <>
      <Helmet>
        <title>ContactTab | Establishment List Updaate </title>
      </Helmet>
      <form onSubmit={handleSubmit}>
        <Grid
          container
          spacing={3}
        >
          {/* Establishment Registration Heading */}
          <Grid
            container
            item
            xs={12}
          >
            <Grid
              item
              xs={8}
            >
              <Typography
                variant="h5"
                align="left"
              >
                Establishment Registration
              </Typography>
            </Grid>
            <Grid
              item
              xs={4}
              className="text-end"
            >
              <EditIcon
                onClick={handleEditClick}
                style={{ cursor: "pointer" }}
              />
            </Grid>
          </Grid>

          {/* Establishment Details */}
          <Grid
            item
            xs={12}
            className="mt-3"
          >
            <Typography
              variant="h6"
              align="left"
            >
              Establishment Details
            </Typography>
          </Grid>
          {/* Divider line */}
          <Grid
            item
            xs={12}
          >
            <Box>
              <Divider
                variant="fullWidth"
                style={{ backgroundColor: "black" }}
              />
            </Box>
          </Grid>
          {/* -----------input fields---------------------------------------- */}
          <Grid
            item
            xs={12}
            className="text-center"
            lg={6}
            md={6}
            sm={12}
          >
            <Grid
              container
              alignItems="center"
            >
              <Grid
                item
                xs={6}
                style={{ textAlign: "left" }}
              >
                <Typography variant="subtitle1">
                  Establishment Unique Number
                </Typography>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  disabled
                  fullWidth
                  name="establishmentid"
                  value={companyData.establishmentid}
                  onChange={handleInputChange}
                  size="small"
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            className="text-center"
            lg={6}
            md={6}
            sm={12}
          >
            <Grid
              container
              alignItems="center"
            >
              <Grid
                item
                xs={6}
                style={{ textAlign: "left" }}
              >
                <Typography variant="subtitle1">
                  Establishment Number
                </Typography>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  fullWidth
                  name="establishmentnumber"
                  value={companyData.establishmentnumber}
                  onChange={handleInputChange}
                  size="small"
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            className="text-center"
            lg={6}
            md={6}
            sm={12}
          >
            <Grid
              container
              alignItems="center"
            >
              <Grid
                item
                xs={6}
                style={{ textAlign: "left" }}
              >
                <Typography variant="subtitle1">
                  Establishment Description
                </Typography>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  fullWidth
                  name="establishmentdescription"
                  value={companyData.establishmentdescription}
                  onChange={handleInputChange}
                  size="small"
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            className="text-center"
            lg={6}
            md={6}
            sm={12}
          >
            <Grid
              container
              alignItems="center"
            >
              <Grid
                item
                xs={6}
                style={{ textAlign: "left" }}
              >
                <Typography variant="subtitle1">Website</Typography>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  fullWidth
                  name="website"
                  value={companyData.website}
                  onChange={handleInputChange}
                  size="small"
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* ----------------Contact Details ---------------- */}
          <Grid
            item
            xs={12}
            className="pt-5"
          >
            <Typography
              variant="h6"
              align="left"
            >
              Contact Details
            </Typography>
          </Grid>
          {/* Divider line */}
          <Grid
            item
            xs={12}
          >
            <Box>
              <Divider
                variant="fullWidth"
                style={{ backgroundColor: "black" }}
              />
            </Box>
          </Grid>
          {/* -----------input fields---------------------------------------- */}
          <Grid
            item
            xs={12}
            lg={6}
            md={6}
            sm={12}
          >
            <Grid
              container
              alignItems="center"
            >
              <Grid
                item
                xs={6}
                style={{ textAlign: "left" }}
              >
                <Typography variant="subtitle1">Email Id</Typography>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  fullWidth
                  size="small"
                  name="emailid"
                  value={companyData.emailid || ""}
                  onChange={handleInputChange}
                  error={errors.emailid} // Show error style if true
                  helperText={errors.emailid ? "Email ID is required" : ""}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            lg={6}
            md={6}
            sm={12}
          >
            <Grid
              container
              alignItems="center"
            >
              <Grid
                item
                xs={6}
                style={{ textAlign: "left" }}
              >
                <Typography variant="subtitle1">Phone Number</Typography>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  fullWidth
                  name="ephonenumber"
                  value={companyData.ephonenumber}
                  size="small"
                  onChange={handleInputChange}
                  error={errors.ephonenumber} // Show error style if true
                  helperText={
                    errors.ephonenumber ? "Phone Number is required" : ""
                  }
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            lg={6}
            md={6}
            sm={12}
            className=""
          >
            <Grid
              container
              alignItems="center"
            >
              <Grid
                item
                xs={6}
                style={{ textAlign: "left" }}
              >
                <Typography variant="subtitle1">Address</Typography>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  size="small"
                  fullWidth
                  name="address1"
                  value={companyData.address1}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            lg={6}
            md={6}
            sm={12}
            className=""
          >
            <Grid
              container
              alignItems="center"
            >
              <Grid
                item
                xs={6}
                style={{ textAlign: "left" }}
              >
                <Typography variant="subtitle1">City</Typography>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  fullWidth
                  size="small"
                  name="city"
                  value={companyData.city}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            lg={6}
            md={6}
            sm={12}
            className=""
          >
            <Grid
              container
              alignItems="center"
            >
              <Grid
                item
                xs={6}
                style={{ textAlign: "left" }}
              >
                <Typography variant="subtitle1">State</Typography>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  fullWidth
                  size="small"
                  name="state"
                  value={companyData.state}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            lg={6}
            md={6}
            sm={12}
            className=""
          >
            <Grid
              container
              alignItems="center"
            >
              <Grid
                item
                xs={6}
                style={{ textAlign: "left" }}
              >
                <Typography variant="subtitle1">Pincode</Typography>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  fullWidth
                  size="small"
                  name="pincode"
                  value={companyData.pincode}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            lg={6}
            md={6}
            sm={12}
            className=""
          >
            <Grid
              container
              alignItems="center"
            >
              <Grid
                item
                xs={6}
                style={{ textAlign: "left" }}
              >
                <Typography variant="subtitle1">Contact Person Name</Typography>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  fullWidth
                  size="small"
                  name="contactpersonname"
                  value={companyData.contactpersonname}
                  onChange={handleInputChange}
                  error={errors.contactpersonname} // Show error style if true
                  helperText={
                    errors.contactpersonname
                      ? "Contact Person Name is required"
                      : ""
                  }
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            lg={6}
            md={6}
            sm={12}
            className=""
          >
            <Grid
              container
              alignItems="center"
            >
              <Grid
                item
                xs={6}
                style={{ textAlign: "left" }}
              >
                <Typography variant="subtitle1">
                  Contact Person E-Mail
                </Typography>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  fullWidth
                  size="small"
                  name="contactpersonemailid"
                  value={companyData.contactpersonemailid}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* -----------License Details --------------*/}
          <Grid
            item
            xs={12}
            className="pt-5 mt-4"
          >
            <Typography
              variant="h6"
              align="left"
            >
              License Details
            </Typography>
          </Grid>
          {/* Divider line */}
          <Grid
            item
            xs={12}
          >
            <Box>
              <Divider
                variant="fullWidth"
                style={{ backgroundColor: "black" }}
              />
            </Box>
          </Grid>
          {/* -----------input fields---------------------------------------- */}
          <Grid
            item
            xs={12}
            lg={6}
            md={6}
            sm={12}
          >
            <Grid
              container
              alignItems="center"
            >
              <Grid
                item
                xs={6}
                style={{ textAlign: "left" }}
              >
                <Typography variant="subtitle1">Industry Type</Typography>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  fullWidth
                  size="small"
                  name="industrytype"
                  value={companyData.industrytype}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Registration Date Field */}
          <Grid
            item
            xs={12}
            lg={6}
            md={6}
            sm={12}
            className=""
          >
            <Grid
              container
              alignItems="center"
            >
              <Grid
                item
                xs={6}
                style={{ textAlign: "left" }}
              >
                <Typography variant="subtitle1">Registration Date</Typography>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={
                      companyData.registrationdate
                        ? dayjs(companyData.registrationdate)
                        : null
                    }
                    onChange={handleDateChange}
                    renderInput={(params) => <TextField {...params} />}
                    disabled={!editMode}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            lg={6}
            md={6}
            sm={12}
            className=""
          >
            <Grid
              container
              alignItems="center"
            >
              <Grid
                item
                xs={6}
                style={{ textAlign: "left" }}
              >
                <Typography variant="subtitle1">Amount Paid</Typography>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  fullWidth
                  size="small"
                  name="amountpaid"
                  value={companyData.amountpaid}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            lg={6}
            md={6}
            sm={12}
            className=""
          >
            <Grid
              container
              alignItems="center"
            >
              <Grid
                item
                xs={6}
                style={{ textAlign: "left" }}
              >
                <Typography variant="subtitle1">Payment Details</Typography>
              </Grid>
              <Grid
                item
                xs={6}
              >
                <TextField
                  size="small"
                  fullWidth
                  name="paymentdetails"
                  value={companyData.paymentdetails}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            className="text-center mt-5"
          >
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!editMode}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
      {/* Success Snackbar */}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openSuccess}
        autoHideDuration={3000}
        onClose={handleSuccessClose}
        message={successMessage}
      />
    </>
  );
};

export default EditCompany;
